import React, { useState, useEffect } from 'react';
import { getTemplateQuestionCount, getViewSurveyQuestions } from '../../api/moduleApi';
import SurveyPreviewModal from './SurveyPreview.jsx';

// --- Main ApprovalRequestCard Component ---
const ApprovalRequestCard = ({ request, onApprove, onDeny }) => {

    const [showDenyForm, setShowDenyForm] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [leaderCount, setLeaderCount] = useState(null);
    const [mainCount, setMainCount] = useState(null);
    const [adhocCount, setAdhocCount] = useState(null);
    const [isLoadingCounts, setIsLoadingCounts] = useState(true);

    const [isViewing, setIsViewing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [isFetchingPreview, setIsFetchingPreview] = useState(false);

    const {
        request_id, module_name, first_name, last_name, requested_at,
        main_template_id, leader_template_id, adhoc_template_id, isProcessing,
        main_template_name, leader_template_name, adhoc_template_name
    } = request;

    useEffect(() => {
        const fetchCounts = async () => {
            setIsLoadingCounts(true);

            const safeFetchCount = async (templateId) => {
                if (!templateId) return 0;
                try {
                    const response = await getTemplateQuestionCount(templateId);

                    // extract 'question_count' from the response object
                    if (response && typeof response === 'object' && response.question_count !== undefined) {
                        return response.question_count;
                    }

                    // fallback if the structure isn't right
                    return 0;
                } catch (error) {
                    console.error("Failed to fetch count:", templateId, error);
                    return 'Error'; // return 'Error' to show it on the card
                }
            };

            const [fetchedMainCount, fetchedLeaderCount, fetchedAdhocCount] = await Promise.all([
                safeFetchCount(main_template_id),
                safeFetchCount(leader_template_id),
                safeFetchCount(adhoc_template_id)
            ]);

            setMainCount(fetchedMainCount);
            setLeaderCount(fetchedLeaderCount);
            setAdhocCount(fetchedAdhocCount);
            setIsLoadingCounts(false);
        };

        fetchCounts();
    }, [leader_template_id, adhoc_template_id]);

    // fetch and show the combined survey preview
    const handleViewPreview = async () => {
        setIsFetchingPreview(true);
        setPreviewData(null);
        try {
            const data = await getViewSurveyQuestions({ requestId: request_id });
            setPreviewData(data);
            setIsViewing(true);
        } catch (error) {
            alert(`Failed to load survey preview: ${error.message}. Check browser console for network details.`);
            console.error("View Preview Error:", error);
        } finally {
            setIsFetchingPreview(false);
        }
    };

    const handleDeny = () => {
        if (feedback.trim() === '') {
            alert("Feedback is required to deny the request.");
            return;
        }
        onDeny(request_id, 'deny', feedback);
        setShowDenyForm(false);
        setFeedback('');
    };

    const handleApprove = () => {
        // confirmation before deploying a live survey
        if (window.confirm(`Are you sure you want to APPROVE the survey for ${module_name}?`)) {
            onApprove(request_id, 'approve', null); // feedback is null for approval
        }
    };

    return (
        <>
            <div className={`p-4 border rounded-xl shadow-md transition ${isProcessing ? 'opacity-50 pointer-events-none' : 'bg-white border-indigo-200 hover:shadow-lg'}`}>
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h3 className="text-xl font-bold text-indigo-800">{module_name}</h3>
                        <p className="text-sm text-gray-600">
                            Requested by: {first_name} {last_name} <span className="ml-4 text-xs text-gray-500">({new Date(requested_at).toLocaleDateString()} at {new Date(requested_at).toLocaleTimeString()})</span>
                        </p>
                    </div>
                    <div className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                        Request ID: {request_id}
                    </div>
                </div>

                <div className="mb-4">
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Template Components:</h4>
                    {/* simplified list output */}
                    <ul className="list-disc list-inside text-sm text-gray-600 ml-4">
                        <li>
                            {main_template_name || 'Master Template'} ({isLoadingCounts ? 'Loading...' : (mainCount !== null ? `${mainCount} question${mainCount === 1 ? '' : 's'}` : 'N/A')})
                        </li>

                        {leader_template_id && (
                            <li>
                                {leader_template_name || "Module Leader's Template"} ({
                                    isLoadingCounts
                                        ? 'Loading...'
                                        : (leaderCount !== null
                                            ? `${leaderCount} question${leaderCount === 1 ? '' : 's'}`
                                            : 'N/A')
                                })
                            </li>
                        )}

                        {adhoc_template_id && (
                            <li>
                                {adhoc_template_name || 'Ad-Hoc Questions Template'} ({
                                    isLoadingCounts
                                        ? 'Loading...'
                                        : (adhocCount !== null
                                            ? `${adhocCount} question${adhocCount === 1 ? '' : 's'}`
                                            : 'N/A')
                                })
                            </li>
                        )}
                    </ul>
                </div>

                {/* --- view button --- */}
                <div className="flex space-x-3 mb-4">
                    <button
                        onClick={handleViewPreview}
                        disabled={isFetchingPreview || isProcessing}
                        className="w-full bg-indigo-500 text-white font-semibold py-2 rounded-lg hover:bg-indigo-600 transition disabled:opacity-75"
                    >
                        {isFetchingPreview ? 'Loading Preview...' : 'View Questionnaire'}
                    </button>
                </div>

                {/* ... approve/deny buttons (and deny form) */}
                <div className="flex space-x-3">
                    <button
                        onClick={handleApprove}
                        disabled={isProcessing}
                        className="flex-1 bg-green-600 text-white font-semibold py-2 rounded-lg hover:bg-green-700 transition"
                    >
                        {isProcessing ? 'Approving...' : 'Approve For Scheduled Deployment'}
                    </button>
                    <button
                        onClick={() => setShowDenyForm(!showDenyForm)}
                        disabled={isProcessing}
                        className="bg-red-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-600 transition"
                    >
                        Deny
                    </button>
                </div>

                {showDenyForm && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        {/* ... deny form content ... */}
                        <p className="text-sm font-semibold text-red-800 mb-2">Denial Feedback (Required)</p>
                        <textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="Explain clearly why the survey cannot be approved (e.g., inappropriate ad-hoc questions)."
                            rows="2"
                            className="w-full p-2 border border-red-300 rounded-lg text-sm focus:ring-red-500 focus:border-red-500"
                        />
                        <button
                            onClick={handleDeny}
                            className="mt-2 w-full bg-red-600 text-white font-semibold py-2 rounded-lg hover:bg-red-700 transition"
                        >
                            Confirm Denial
                        </button>
                    </div>
                )}
            </div>

            {/* render the modal if isViewing is true */}
            {isViewing && (
                <SurveyPreviewModal
                    requestData={previewData}
                    onClose={() => setIsViewing(false)}
                />
            )}
        </>
    );
};

export default ApprovalRequestCard;